import React from 'react';
import { useNavigate } from 'react-router-dom';
import { PATH } from '@/router/PATH';
import { TONE_BY_ZONE, type OverviewTone } from '../overviewMeta';
import { useOverviewMetric } from '../useOverviewMetric';

export type NetWorkingCapitalData = {
    tone: OverviewTone;
    /** Integer tetri, all four. */
    assetsTetri: number;
    liabilitiesTetri: number;
    differenceTetri: number;
    /** Null when there was no prior month to compare against. */
    deltaTetri: number | null;
};

/**
 * Net working capital: assets against what falls due, and the room between.
 *
 * The bands that decide the tone live in the API — they are business policy, and
 * a second copy here would let this card and the assessment rail describe the
 * same business differently.
 *
 * Alone among the four, this card's empty-state action goes somewhere real:
 * working capital is read off the chart of accounts, so "Open chart of accounts"
 * is both the explanation and the fix.
 */
export const useNetWorkingCapital = () => {
    const { metric, error, isLoading, missing, onRetry } =
        useOverviewMetric('netWorkingCapital');

    const data = React.useMemo<NetWorkingCapitalData | null>(() => {
        if (!metric || metric.state !== 'COMPUTED') {
            return null;
        }
        const { tone, assetsTetri, liabilitiesTetri, differenceTetri } = metric;
        // COMPUTED with a figure missing is contract drift. Render the empty
        // card rather than a card with a hole in it — a metric that cannot show
        // both sides of its own subtraction should not show the answer.
        if (
            tone === null ||
            assetsTetri === null ||
            liabilitiesTetri === null ||
            differenceTetri === null
        ) {
            return null;
        }
        return {
            tone: TONE_BY_ZONE[tone],
            assetsTetri,
            liabilitiesTetri,
            differenceTetri,
            deltaTetri: metric.deltaTetri,
        };
    }, [metric]);

    const navigate = useNavigate();
    const onEmptyAction = React.useCallback(
        () => navigate(PATH.ACCOUNTING.BOOKS.ACCOUNTS),
        [navigate],
    );

    return { data, error, isLoading, missing, onRetry, onEmptyAction };
};
